import { useId, type ReactNode } from 'react'
import { cn } from '../../utils/cn'
import { COUNTRY_CODE, toNationalDigits } from '../../utils/phone'
import { FormField } from './FormField'
import { describedBy } from './fieldStyles'

export interface PhoneInputProps {
  label: ReactNode
  /** National digits only — no country code, no punctuation. */
  value: string
  onChange: (nationalDigits: string) => void
  id?: string
  hint?: ReactNode
  error?: ReactNode
  required?: boolean
  autoFocus?: boolean
  containerClassName?: string
}

/* Same box as CONTROL_BASE, but the ring has to answer to the wrapper: the
   country code sits inside the border, so focus lives on a child. The tones are
   spelled out rather than derived from `controlTone` because Tailwind only sees
   class names it can read literally in the source. */
const WRAPPER_BASE =
  'flex w-full items-center gap-3 rounded-lg border bg-surface-container-lowest px-4 py-3 transition-colors focus-within:ring-1'

const WRAPPER_OK = 'border-outline-variant focus-within:border-primary focus-within:ring-primary'
const WRAPPER_ERROR = 'border-error focus-within:border-error focus-within:ring-error'

/**
 * Phone field with the country code as furniture rather than as something to
 * type — the mechanism `cherrybooking-dashboard` uses in `PhoneCAInput`.
 *
 * The customer cannot omit the country code because they never enter it, and
 * cannot double it either: paste keeps the national digits and drops a trunk
 * `1`, so "+1 (415) 555-0132" and "4155550132" both land as "4155550132".
 *
 * That only holds without a `maxLength`. The attribute counts characters as the
 * browser receives them, punctuation included, so it truncated a pasted
 * "+19012456721" to the first ten characters — "+190124567" — and handed
 * `toNationalDigits` nine digits behind a trunk `1` it could no longer
 * recognise. The length cap belongs where the digits are already clean:
 * `toNationalDigits` slices to `NATIONAL_DIGITS`, and the field is controlled,
 * so typing an eleventh digit still cannot land.
 */
export function PhoneInput({
  label,
  value,
  onChange,
  id,
  hint,
  error,
  required,
  autoFocus,
  containerClassName,
}: PhoneInputProps) {
  const autoId = useId()
  const fieldId = id ?? autoId

  return (
    <FormField
      id={fieldId}
      label={label}
      hint={hint}
      error={error}
      required={required}
      className={containerClassName}
    >
      <div className={cn(WRAPPER_BASE, error ? WRAPPER_ERROR : WRAPPER_OK)}>
        <span aria-hidden="true" className="material-symbols-outlined text-[20px] text-outline">
          call
        </span>
        {/* Announced to screen readers through the input's own aria-label, not
            here, so the code is not read as a separate field. */}
        <span aria-hidden="true" className="font-body-md text-body-md text-on-surface-variant">
          {COUNTRY_CODE}
        </span>
        <span aria-hidden="true" className="h-5 w-px shrink-0 bg-outline-variant" />
        <input
          id={fieldId}
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          autoFocus={autoFocus}
          required={required}
          value={value}
          placeholder="415 555 0132"
          aria-label={`Phone number, country code ${COUNTRY_CODE}`}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(fieldId, hint, error)}
          onChange={(event) => onChange(toNationalDigits(event.target.value))}
          className="min-w-0 flex-1 bg-transparent font-body-md text-body-md text-on-surface placeholder:text-outline focus:outline-none"
        />
      </div>
    </FormField>
  )
}
